/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import toast from 'react-hot-toast';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Button, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import ChargeShareIcon from '@stack/assets/icons/charge-share.svg';
import CheckCircleIcon from '@stack/assets/icons/check-icon.png';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { BorderTitle } from '@stack/ui/components';
import isEmpty from 'lodash/isEmpty';
import Image from 'next/image';

import QuoteDialog from './QuoteDialog';

export type CarHighlightBoxProps = {
  new_car_key_detail: {
    new_car_key_detail_description: string;
  }[];
  data: {
    carId: number;
    carImage: any;
    carName: string;
    lowestPrice: string;
    highestPrice: string;
    carBrandImage: any;
    isEv: boolean;
  };
  location_state: [];
  location_city: [];
  webView?: boolean;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const CarHighlightBox: (
  props: CarHighlightBoxProps
) => null | JSX.Element = (props) => {
  const {
    new_car_key_detail,
    data,
    location_city,
    location_state,
    webView = false,
  } = props;

  const { clickAction } = usePostMsgContext();
  const [isOpen, setIsOpen] = useState(false);
  const { basePath } = createBasePath({ webView, type: 'cars' });
  const currentPath = `${basePath}`;
  const isEmptyData = isEmpty(new_car_key_detail);

  // ======================= VIEWS
  const renderContent = (content: string) => {
    return (
      <Stack flexDirection="row" gap={2}>
        <Box style={{ width: 16, height: 16, minWidth: 16, marginTop: 1.5 }}>
          <Image src={CheckCircleIcon} alt={'check'} width={16} height={16} />
        </Box>
        <Typography fontWeight={500} fontSize={'0.875rem'}>
          {content}
        </Typography>
      </Stack>
    );
  };

  // ============ EVENTS
  const onOpenGetQuoteDialog = () => {
    setIsOpen(true);
    if (webView) {
      clickAction(currentPath, false, {
        title: 'Get Quote',
        params: {
          headerShown: false,
          action: 'headerShown',
        },
      });
    }
  };

  const onClose = () => {
    setIsOpen(false);
    if (webView) {
      clickAction(currentPath, false, {
        title: 'Get Quote',
        params: {
          headerShown: true,
          action: 'headerShown',
        },
      });
    }
  };

  const handleLinkClick = () => {
    const url = 'chargeshare://';
    const timeout = 3000; // Adjust as needed

    let hasFailed = false;

    const appOpenTimeout = setTimeout(() => {
      hasFailed = true;
      toast.error('App no found');
    }, timeout);

    window.location.href = url;

    window.addEventListener('blur', () => {
      clearTimeout(appOpenTimeout);
      if (!hasFailed) {
        window.removeEventListener('blur', () => {});
      }
    });
  };

  if (isEmptyData) return null;
  return (
    <div>
      <QuoteDialog
        isOpen={isOpen}
        onClose={onClose}
        data={{
          carId: data?.carId,
          carImage: data?.carImage,
          carName: data?.carName,
          carBrandImage: data?.carBrandImage,
          lowestPrice: data?.lowestPrice,
          highestPrice: data?.highestPrice,
        }}
        location_state={location_state}
        location_city={location_city}
        webView={webView}
      />
      <Stack
        flexDirection="row"
        justifyContent={'space-between'}
        sx={{ alignItems: 'center' }}
      >
        <BorderTitle title={'Highlights'} />
        {false && (
          <Button
            variant="outlined"
            startIcon={<ChargeShareIcon />}
            endIcon={<ChevronRightIcon />}
            color={'secondary'}
            style={{
              borderRadius: 4,
              borderColor: '#EDECED',
              color: '#1E242F',
              textTransform: 'capitalize',
              fontWeight: 500,
            }}
            sx={{
              fontSize: '0.875rem',
            }}
            onClick={handleLinkClick}
          >
            Find EV
          </Button>
        )}
      </Stack>
      <Stack sx={{ paddingTop: 5 }}>
        <Stack
          padding={4}
          justifyContent={'center'}
          border={'1px solid #EDECED'}
          gap={2}
          // {...(isEmptyData && {
          //   minHeight: 200,
          //   padding: '16px',
          // })}
        >
          {isEmptyData ? (
            <Typography
              width="100%"
              textAlign={'center'}
              fontWeight={400}
              fontSize={'0.875rem'}
            >
              Highlight updates coming soon. Stay tuned for more details.
            </Typography>
          ) : (
            new_car_key_detail?.map((item, index) => (
              <Stack key={index}>
                {renderContent(item.new_car_key_detail_description)}
              </Stack>
            ))
          )}
        </Stack>
        <Button
          sx={{
            borderRadius: 0,
            fontWeight: 700,
            boxShadow: 'none',
          }}
          color={'secondary'}
          variant="contained"
          onClick={onOpenGetQuoteDialog}
        >
          Get Quote
        </Button>
      </Stack>
    </div>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default CarHighlightBox;
